import { useEffect, useMemo, useState } from "react";
import { Send } from "lucide-react";
import { Reveal } from "./Reveal";
import { SERVICE_CATEGORIES } from "../data/services";
import { PHONE_MOBILE_DISPLAY, PHONE_MOBILE_TEL } from "../data/site";

const TIME_SLOTS = [
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "01:00 PM",
  "02:00 PM",
  "03:00 PM",
  "04:00 PM",
  "05:00 PM",
  "06:00 PM",
  "07:00 PM",
  "08:00 PM",
];

const STEPS = [
  "Choose a service from the menu",
  "Pick a date and time that suits you",
  "Confirm your booking on WhatsApp",
];

const FIELD_CLASS =
  "w-full rounded-xl border border-[rgba(242,239,230,0.15)] bg-[#383D33] px-4 py-3 font-body text-sm text-[#F2EFE6] placeholder-[#F2EFE6]/40 outline-none transition-colors duration-300 focus:border-[#C9A24B]";
const LABEL_CLASS =
  "mb-2 block font-body text-[11px] font-medium uppercase tracking-[0.25em] text-[#C9A24B]";

const initialForm = { name: "", phone: "", category: "", service: "", date: "", time: "" };

export const Booking = () => {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");

  const services = useMemo(
    () => SERVICE_CATEGORIES.find((c) => c.id === form.category)?.items ?? [],
    [form.category]
  );
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const onPrefill = (event) => {
      const id = event.detail;
      if (SERVICE_CATEGORIES.some((c) => c.id === id)) {
        setForm((prev) => ({ ...prev, category: id, service: "" }));
        setError("");
      }
    };
    window.addEventListener("prefill-booking-category", onPrefill);
    return () => window.removeEventListener("prefill-booking-category", onPrefill);
  }, []);

  const update = (key) => (e) => {
    setForm((prev) => ({
      ...prev,
      [key]: e.target.value,
      ...(key === "category" ? { service: "" } : {}),
    }));
    setError("");
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.category || !form.service || !form.date || !form.time) {
      setError("Please fill in your name, service, preferred date and time.");
      return;
    }
    const categoryName = SERVICE_CATEGORIES.find((c) => c.id === form.category)?.name;
    const lines = [
      "Hello Her Styles Ladies Salon, I would like to book an appointment.",
      `Name: ${form.name}`,
      form.phone ? `Phone: ${form.phone}` : null,
      `Service: ${form.service} (${categoryName})`,
      `Preferred Date: ${form.date}`,
      `Preferred Time: ${form.time}`,
    ].filter(Boolean);
    window.open(
      `https://wa.me/971565265295?text=${encodeURIComponent(lines.join("\n"))}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="grain relative scroll-mt-20 bg-[#2B2F26] py-24 sm:py-28"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-[rgba(201,162,75,0.3)] bg-[#454538] p-8 shadow-2xl sm:p-12">
            <img
              src="/logo.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute -right-8 -top-8 w-56 opacity-10"
            />

            <div className="relative grid gap-12 lg:grid-cols-2 lg:gap-16">
              <div>
                <p className="font-body text-xs font-medium uppercase tracking-[0.35em] text-[#C9A24B]">
                  Book an Appointment
                </p>
                <h2 className="mt-4 font-brand text-3xl tracking-wide text-[#F2EFE6] sm:text-4xl">
                  Reserve Your Chair
                </h2>
                <div className="mt-5 flex items-center gap-3">
                  <span className="gold-hairline h-px w-16" />
                  <span className="h-1.5 w-1.5 rotate-45 bg-[#C9A24B]" />
                </div>
                <p className="mt-5 max-w-md font-body text-sm leading-relaxed text-[#F2EFE6]/75 sm:text-base">
                  Tell us what you are dreaming of and when you would like to visit. Your
                  request opens straight in WhatsApp, and we confirm your slot within minutes.
                </p>

                <ol className="mt-8 space-y-4">
                  {STEPS.map((step, i) => (
                    <li key={step} className="flex items-center gap-4">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[rgba(201,162,75,0.5)] font-brand text-base text-[#C9A24B]">
                        {i + 1}
                      </span>
                      <span className="font-body text-sm tracking-wide text-[#F2EFE6]/80">
                        {step}
                      </span>
                    </li>
                  ))}
                </ol>

                <p className="mt-8 font-body text-sm text-[#F2EFE6]/70">
                  Prefer to talk? Call us on{" "}
                  <a
                    href={PHONE_MOBILE_TEL}
                    data-testid="booking-call-link"
                    className="text-[#C9A24B] underline underline-offset-4 transition-colors duration-300 hover:text-[#D4AF37]"
                  >
                    {PHONE_MOBILE_DISPLAY}
                  </a>
                </p>
              </div>

              <form onSubmit={submit} data-testid="booking-form" className="grid content-start gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label htmlFor="booking-name" className={LABEL_CLASS}>
                    Your Name
                  </label>
                  <input
                    id="booking-name"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="e.g. Aisha Khan"
                    data-testid="booking-name-input"
                    className={FIELD_CLASS}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="booking-phone" className={LABEL_CLASS}>
                    Phone (optional)
                  </label>
                  <input
                    id="booking-phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="So we can reach you if needed"
                    data-testid="booking-phone-input"
                    className={FIELD_CLASS}
                  />
                </div>

                <div>
                  <label htmlFor="booking-category" className={LABEL_CLASS}>
                    Category
                  </label>
                  <select
                    id="booking-category"
                    value={form.category}
                    onChange={update("category")}
                    data-testid="booking-category-select"
                    className={FIELD_CLASS}
                  >
                    <option value="">Select category</option>
                    {SERVICE_CATEGORIES.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="booking-service" className={LABEL_CLASS}>
                    Service
                  </label>
                  <select
                    id="booking-service"
                    value={form.service}
                    onChange={update("service")}
                    disabled={!form.category}
                    data-testid="booking-service-select"
                    className={`${FIELD_CLASS} disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    <option value="">
                      {form.category ? "Select service" : "Pick a category first"}
                    </option>
                    {services.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="booking-date" className={LABEL_CLASS}>
                    Preferred Date
                  </label>
                  <input
                    id="booking-date"
                    type="date"
                    min={today}
                    value={form.date}
                    onChange={update("date")}
                    data-testid="booking-date-input"
                    style={{ colorScheme: "dark" }}
                    className={FIELD_CLASS}
                  />
                </div>

                <div>
                  <label htmlFor="booking-time" className={LABEL_CLASS}>
                    Preferred Time
                  </label>
                  <select
                    id="booking-time"
                    value={form.time}
                    onChange={update("time")}
                    data-testid="booking-time-select"
                    className={FIELD_CLASS}
                  >
                    <option value="">Select time</option>
                    {TIME_SLOTS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                {error && (
                  <p
                    data-testid="booking-form-error"
                    className="sm:col-span-2 rounded-xl border border-[rgba(232,180,180,0.3)] bg-[rgba(232,180,180,0.08)] px-4 py-3 font-body text-sm text-[#E8B4B4]"
                  >
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="booking-submit-button"
                  className="btn-shimmer flex items-center justify-center gap-2.5 rounded-full bg-[#C9A24B] px-8 py-4 font-body text-xs font-semibold uppercase tracking-[0.2em] text-[#383D33] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#D4AF37] sm:col-span-2"
                >
                  <Send className="h-4 w-4" />
                  Confirm on WhatsApp
                </button>
                <p className="text-center font-body text-xs text-[#F2EFE6]/50 sm:col-span-2">
                  You will be redirected to WhatsApp to send your request.
                </p>
              </form>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
